import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Separator } from '../components/ui/separator';
import { Shield, Eye, EyeOff, Loader2, Sun, Moon, Building2 } from 'lucide-react';

const DARK_LOGO = "https://customer-assets.emergentagent.com/job_vaptara-vuln-ai/artifacts/znyb36lz_vaptara_logo_w.png";
const LIGHT_LOGO = "https://customer-assets.emergentagent.com/job_vaptara-vuln-ai/artifacts/z7et0mch_Vaptara%20logo%20cropped.png";

// SSO Provider Icons
const GoogleIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const MicrosoftIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path d="M11.4 11.4H0V0h11.4v11.4z" fill="#f25022"/>
    <path d="M24 11.4H12.6V0H24v11.4z" fill="#7fba00"/>
    <path d="M11.4 24H0V12.6h11.4V24z" fill="#00a4ef"/>
    <path d="M24 24H12.6V12.6H24V24z" fill="#ffb900"/>
  </svg>
);

const GitHubIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current">
    <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0 1 12 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
  </svg>
);

const OktaIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <circle cx="12" cy="12" r="10" fill="#007dc1"/>
    <circle cx="12" cy="12" r="4" fill="white"/>
  </svg>
);

const AzureIcon = () => (
  <svg viewBox="0 0 24 24" className="h-5 w-5">
    <path d="M6.5 3L13 20.5l7-4.5L8.5 3H6.5z" fill="#0078d4"/>
    <path d="M13 20.5L6.5 17l-.5-2.5L13 20.5z" fill="#50e6ff"/>
    <path d="M6.5 3L6 14.5l7 6L6.5 3z" fill="#50e6ff"/>
  </svg>
);

export default function LoginPage() {
  const { login, register, loginWithGoogle } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next') && searchParams.get('next').startsWith('/') ? searchParams.get('next') : '/dashboard';
  const defaultTab = searchParams.get('mode') === 'register' ? 'register' : 'login';
  const [isLoading, setIsLoading] = useState(false);
  const [ssoLoading, setSsoLoading] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showEnterpriseSso, setShowEnterpriseSso] = useState(false);
  const [error, setError] = useState('');
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ name: '', email: '', password: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(loginData.email, loginData.password);
      navigate(nextPath);
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await register(registerData.email, registerData.password, registerData.name);
      navigate(nextPath);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSsoLogin = async (provider) => {
    setSsoLoading(provider);
    try {
      if (provider === 'google') {
        await loginWithGoogle();
      } else {
        // For other SSO providers - they would integrate similarly
        // For now, show a message that enterprise SSO needs configuration
        setError(`${provider.charAt(0).toUpperCase() + provider.slice(1)} SSO requires enterprise configuration. Contact sales@vaptara.com`);
        setSsoLoading(null);
      }
    } catch (err) {
      setError('SSO login failed. Please try again.');
      setSsoLoading(null);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Branding panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden items-center justify-center bg-[#070B1A] noise">
        <div className="orb orb-indigo animate-float w-[520px] h-[520px] -top-40 -left-32" />
        <div className="orb orb-cyan animate-float-slow w-[420px] h-[420px] bottom-[-120px] right-[-80px]" />
        <div className="absolute inset-0 bg-grid opacity-60" />
        <div className="relative z-10 px-12 max-w-xl">
          <img src={DARK_LOGO} alt="Vaptara" className="h-12 mb-10" />
          <h1 className="text-4xl xl:text-5xl font-heading font-extrabold text-white tracking-tight leading-[1.05]">
            Security findings you can <span className="text-gradient">prioritize.</span>
          </h1>
          <p className="text-base text-white/65 leading-relaxed mt-5 max-w-md">
            AI-driven vulnerability management — from free online scanners to Entra ID posture, architecture reviews and assurance scoring.
          </p>
          <div className="mt-10 relative rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-indigo-500/20 max-w-md animate-float">
            <img src="/illustrations/scanning.jpg" alt="" className="w-full aspect-[4/3] object-cover" />
          </div>
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            {[['8', 'Scanner engines'], ['6', 'Pipeline phases'], ['3', 'Assurance frameworks']].map(([v, l]) => (
              <div key={l} className="p-3 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10">
                <div className="text-2xl font-heading font-bold text-white">{v}</div>
                <div className="text-[11px] text-white/55 mt-0.5">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right - Auth form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background relative">
        <Button
          variant="ghost" size="icon"
          className="absolute top-4 right-4"
          onClick={toggleTheme}
          data-testid="login-theme-toggle"
        >
          {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </Button>

        <Card className="w-full max-w-md border-border/60 shadow-2xl shadow-indigo-500/10 rounded-3xl animate-fade-in-up" data-testid="auth-card">
          <CardHeader className="text-center pb-4 pt-8">
            <div className="lg:hidden flex justify-center mb-4">
              <img src={theme === 'dark' ? DARK_LOGO : LIGHT_LOGO} alt="Vaptara" className="h-10" />
            </div>
            <h2 className="text-2xl font-bold font-heading tracking-tight">Welcome to Vaptara</h2>
            <p className="text-sm text-muted-foreground mt-1">Secure your digital assets with AI</p>
          </CardHeader>
          <CardContent className="pb-8 px-8">
            <Tabs defaultValue={defaultTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4" data-testid="auth-tabs">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Sign Up</TabsTrigger>
              </TabsList>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-destructive text-sm" data-testid="auth-error">
                  {error}
                </div>
              )}

              <TabsContent value="login">
                <form onSubmit={handleLogin} className="space-y-4" data-testid="login-form">
                  <div>
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      type="email"
                      placeholder="you@company.com"
                      value={loginData.email}
                      onChange={e => setLoginData(p => ({ ...p, email: e.target.value }))}
                      required
                      data-testid="login-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="login-password">Password</Label>
                    <div className="relative">
                      <Input
                        id="login-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={loginData.password}
                        onChange={e => setLoginData(p => ({ ...p, password: e.target.value }))}
                        required
                        data-testid="login-password-input"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={isLoading} data-testid="login-submit-btn">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Sign In
                  </Button>
                </form>
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4" data-testid="register-form">
                  <div className="p-3 rounded-lg bg-primary/5 border border-primary/20 text-xs text-muted-foreground">
                    <p className="font-medium text-foreground mb-1">14-Day Free Trial</p>
                    <p>1 product, 100 scan credits. Upgrade anytime for unlimited access.</p>
                  </div>
                  <div>
                    <Label htmlFor="reg-name">Full Name</Label>
                    <Input
                      id="reg-name"
                      placeholder="John Doe"
                      value={registerData.name}
                      onChange={e => setRegisterData(p => ({ ...p, name: e.target.value }))}
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-email">Email</Label>
                    <Input
                      id="reg-email"
                      type="email"
                      placeholder="you@company.com"
                      value={registerData.email}
                      onChange={e => setRegisterData(p => ({ ...p, email: e.target.value }))}
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="reg-password">Password</Label>
                    <Input
                      id="reg-password"
                      type="password"
                      placeholder="Min 6 characters"
                      value={registerData.password}
                      onChange={e => setRegisterData(p => ({ ...p, password: e.target.value }))}
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                  <Button type="submit" className="w-full h-11" disabled={isLoading} data-testid="register-submit-btn">
                    {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Create Account
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            <p className="text-center text-xs text-muted-foreground mt-6">
              Not ready to sign up?{' '}
              <Link to="/free-scan" className="text-primary hover:underline font-medium" data-testid="free-scanner-link">Try the free scanner</Link>
              {' '}· <Link to="/plans" className="text-primary hover:underline font-medium" data-testid="login-pricing-link">View plans</Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
